import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay } from 'rxjs';
import * as FboLocationsActions from 'src/app/store/administration/fbo-locations/fbo-locations.actions';
import * as FboLocationsSelectors from 'src/app/store/administration/fbo-locations/fbo-locations.selectors';
import * as UserAuthActions from 'src/app/store/user/auth/user-auth.actions';

@Injectable({
  providedIn: 'root',
})
export class FboLocationService {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private router = inject(Router);

  // Expose store selectors as observables
  locations$ = this.store.select(FboLocationsSelectors.selectLocations);
  selectedLocation$ = this.store.select(FboLocationsSelectors.selectSelectedLocation);
  isLoading$ = this.store.select(FboLocationsSelectors.selectIsLoading);
  isSwitching$ = this.store.select(FboLocationsSelectors.selectIsSwitching);
  error$ = this.store.select(FboLocationsSelectors.selectError);
  isLoadingOrSwitching$ = this.store.select(FboLocationsSelectors.selectIsLoadingOrSwitching);

  constructor() {
    this.loadLocations();

    // Refresh profile and re-render the current route when location is switched successfully
    this.actions$
      .pipe(ofType(FboLocationsActions.switchFboLocationSuccess), delay(1000), takeUntilDestroyed())
      .subscribe(() => {
        // Recall profile API
        this.store.dispatch(UserAuthActions.loadUserDetails());

        // Re-render only router-outlet area by navigating to the current route with a custom reuse strategy
        const currentUrl = this.router.url;
        this.router.routeReuseStrategy.shouldReuseRoute = (future, curr) => {
          if (future.routeConfig === curr.routeConfig) {
            // Force reload only on the leaf route component (where firstChild is null)
            return !!future.firstChild;
          }
          return future.routeConfig === curr.routeConfig;
        };
        this.router.navigateByUrl(currentUrl).then(() => {
          // Restore default RouteReuseStrategy behavior
          this.router.routeReuseStrategy.shouldReuseRoute = (future, curr) =>
            future.routeConfig === curr.routeConfig;
        });
      });
  }

  /**
   * Load FBO locations by dispatching the load action
   */
  loadLocations(): void {
    this.store.dispatch(FboLocationsActions.loadFboLocations());
  }

  /**
   * Switch to a different FBO location by dispatching the switch action
   * @param locationId The ID of the location to switch to
   */
  switchLocation(locationId: number): void {
    this.store.dispatch(FboLocationsActions.switchFboLocation({ locationId }));
  }

  /**
   * Reset FBO locations state
   */
  reset(): void {
    this.store.dispatch(FboLocationsActions.resetFboLocationsState());
  }
}
